/**
 * The closed vocabularies and limits of org-admin announcements (#14617,
 * epic #14616).
 *
 * `Announcement.displayMode`, `Announcement.status` and
 * `Announcement.reminderChannels` are `String` columns (ADR-0107), so these
 * tuples ARE the constraint: nothing in Postgres refuses `'popup'`. Every
 * write goes through `src/lib/announcements/`, which validates against them.
 *
 * CLIENT-SAFE: no `server-only`, no Prisma, nothing but zod and a type import,
 * so the authoring and delivery UI can render the same lists and limits the
 * server enforces.
 */

import { z } from 'zod'

/**
 * `Attachment.attachableType` for an announcement's custom image. The
 * attachment's `attachableId` is the announcement's id — authoring creates
 * the draft row first and uploads against it — and
 * `Announcement.imageAttachmentId` is surfaced to viewers only when it
 * points at such an attachment (`listActiveAnnouncementsForViewer`).
 */
export const ANNOUNCEMENT_ATTACHABLE_TYPE = 'announcement' as const

/**
 * How an announcement is shown: a persistent in-app banner (until dismissed
 * or its CTA is clicked) or a one-time modal (until first seen).
 */
export const ANNOUNCEMENT_DISPLAY_MODES = ['banner', 'modal'] as const
export type AnnouncementDisplayMode =
  (typeof ANNOUNCEMENT_DISPLAY_MODES)[number]
export const announcementDisplayModeSchema = z.enum(ANNOUNCEMENT_DISPLAY_MODES)

/**
 * Lifecycle. Only `live` is ever delivered; `scheduled` is `live`-in-waiting
 * and `archived` is terminal.
 */
export const ANNOUNCEMENT_STATUSES = [
  'draft',
  'scheduled',
  'live',
  'archived',
] as const
export const announcementStatusSchema = z.enum(ANNOUNCEMENT_STATUSES)

/**
 * The statuses whose audience is materialized into recipient rows. A
 * recipient row grants read access to the announcement's image, so a `draft`
 * or an `archived` announcement is never materialized.
 */
export const ANNOUNCEMENT_DELIVERABLE_STATUSES = [
  'scheduled',
  'live',
] as const satisfies readonly (typeof ANNOUNCEMENT_STATUSES)[number][]

/** Channels a reminder may be sent over, picked per announcement. */
export const ANNOUNCEMENT_REMINDER_CHANNELS = [
  'in_app',
  'email',
  'slack',
] as const
export type AnnouncementReminderChannel =
  (typeof ANNOUNCEMENT_REMINDER_CHANNELS)[number]
export const announcementReminderChannelSchema = z.enum(
  ANNOUNCEMENT_REMINDER_CHANNELS,
)

export const ANNOUNCEMENT_TITLE_MAX = 120
export const ANNOUNCEMENT_BODY_MAX = 1000
export const ANNOUNCEMENT_CTA_LABEL_MAX = 40
export const ANNOUNCEMENT_CTA_URL_MAX = 2048

/** `reminderBusinessDays` bounds; NULL means "no reminder". */
export const ANNOUNCEMENT_REMINDER_BUSINESS_DAYS_MIN = 1
export const ANNOUNCEMENT_REMINDER_BUSINESS_DAYS_MAX = 10
export const DEFAULT_ANNOUNCEMENT_REMINDER_BUSINESS_DAYS = 3
